import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { CTASection } from "@/components/CTASection";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";
import { seoTitle, seoDescription } from "@/lib/seo-meta";

const TITLE = seoTitle("Sobre a Liderança com Valores — Plataforma Editorial");
const DESC = seoDescription(
  "Conheça a Liderança com Valores: plataforma editorial independente com artigos, análises e guias sobre liderança, gestão, estratégia e empreendedorismo.",
);

export const Route = createFileRoute("/sobre")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "/sobre" },
    ],
    links: [{ rel: "canonical", href: `${SITE.baseUrl}/sobre` }],
  }),
  component: Sobre,
});

const values = [
  { t: "Conteúdo acionável", d: "Cada artigo entrega algo que o líder pode aplicar já na próxima semana no seu time ou empresa." },
  { t: "Rigor editorial", d: "Buscamos fontes, dados e referências sólidas do mundo dos negócios. Sem clichê motivacional." },
  { t: "Serviço a quem lidera", d: "Escrevemos para quem toma decisões que impactam pessoas, resultados e cultura." },
  { t: "Leitura direta", d: "Texto claro, sem jargão vazio. Priorizamos a experiência de leitura em qualquer dispositivo." },
];

const method = [
  "Pauta é discutida em reunião editorial semanal",
  "Cada artigo tem um editor responsável e passa por revisão",
  "Referências são checadas em fontes primárias, dados públicos e literatura de gestão",
  "Guias práticos incluem exemplos aplicáveis e frameworks reconhecidos",
  "Análises trazem contexto de mercado — não são opiniões avulsas",
  "Correções e atualizações são registradas ao final de cada artigo",
];


function Sobre() {
  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-12 md:pt-20 md:pb-16">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Sobre" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="eyebrow">Sobre a plataforma</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
              <span className="text-gradient">{SITE.name}</span> — conteúdo estratégico para quem lidera.
            </h1>
            <p className="mt-4 text-muted-foreground md:text-lg">
              Somos uma plataforma editorial independente com artigos, análises e guias sobre liderança, gestão, cultura organizacional, estratégia, empreendedorismo e carreira — para líderes, empresas e profissionais nos tempos atuais.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/blog" className="btn-accent">Ler os artigos</Link>
              <Link to="/contato" className="btn-ghost">Falar com o time</Link>
            </div>
          </div>

        </div>
      </section>

      <Section>
        <SectionHeader eyebrow="Nossos valores" title="Princípios da nossa linha editorial" />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <Reveal key={v.t} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <div className="card-glass">
                <h3 className="text-lg font-semibold">{v.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="bg-[color:var(--color-sidebar)]">
        <Reveal>
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <span className="eyebrow">Metodologia</span>
              <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">Como fazemos cada artigo</h2>
              <p className="mt-4 text-muted-foreground">
                Todo conteúdo publicado na Liderança com Valores passa por um fluxo editorial claro: pesquisa, redação, edição e revisão. A meta é entregar informação confiável, útil e de leitura direta.
              </p>
            </div>
            <ul className="grid gap-3">
              {method.map((m) => (
                <li key={m} className="flex items-start gap-3 rounded-md border border-border bg-card p-4">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-foreground text-background">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
                      <path d="m5 12 5 5L20 7" />
                    </svg>
                  </span>
                  <span className="text-sm">{m}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </Section>

      <Section>
        <Reveal>
          <div className="rounded-lg border border-border bg-card p-8 md:p-10">
            <h2 className="text-2xl font-bold">Transparência editorial</h2>
            <p className="mt-4 text-sm text-muted-foreground">
              Artigos podem incluir referências a marcas, produtos ou serviços parceiros — sempre indicados no próprio texto. O critério de recomendação é editorial: indicamos o que consideramos relevante para o leitor.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Encontrou um erro ou tem uma sugestão de pauta? Escreva para <a href={`mailto:${SITE.email}`} className="text-foreground underline font-semibold">{SITE.email}</a>.
            </p>
          </div>
        </Reveal>
      </Section>

      <CTASection title="Novos artigos toda semana" text="Análises, guias e conteúdo estratégico para líderes e empresas." buttonLabel="Ver todos os artigos" />

    </>
  );
}
